import type { UserManager } from "oidc-client-ts";
import { fetchUsageCustomerList, fetchUsageSummary } from "./api-client";
import { getActiveUser } from "./get-active-user";
import { loadRuntimePortalConfig } from "./load-runtime-config";
import type { PortalConfig } from "./portal-config";
import { createUserManager } from "./user-manager-settings";
import {
  buildCustomerSectionsHtml,
  buildDashboardPageHtml,
} from "./dashboard-html";
import { hasPortalRealmRole } from "./access";
import { secondsToHhMm } from "./format-time";
import { escapeHtml } from "./html";
import type { SummaryResponse } from "./api-types";
import { monthNowUtc } from "./utc-month";
import "./styles.css";

/** OIDC profile claims are sometimes mis-mapped; avoid `String(object)` → `[object Object]` in the header. */
function displayNameFromProfile(profile: unknown): string {
  if (!profile || typeof profile !== "object" || Array.isArray(profile)) {
    return "user";
  }
  const p = profile as Record<string, unknown>;
  const a = p.preferred_username ?? p.email;
  if (typeof a === "string" && a.trim()) return a.trim();
  if (typeof a === "number" && Number.isFinite(a)) return String(a);
  return "user";
}

function wireSignOut(root: HTMLElement, mgr: UserManager): void {
  root.querySelector("#logout")?.addEventListener("click", () => {
    void mgr.signoutRedirect();
  });
}

async function startApp(): Promise<void> {
  const maybeRoot = document.querySelector<HTMLElement>("#app");
  if (!maybeRoot) return;
  const host: HTMLElement = maybeRoot;

  host.innerHTML = `
    <div class="card">
      <p class="muted">Loading configuration…</p>
    </div>`;

  let cfg: PortalConfig;
  try {
    cfg = await loadRuntimePortalConfig(
      fetch,
      window.location,
      import.meta.env.BASE_URL || "/",
    );
  } catch (e) {
    host.innerHTML = `<div class="card error"><h1>Configuration</h1><p>${escapeHtml(String(e))}</p><p class="muted">Ensure <code>config.json</code> is deployed at the site root (same origin as this app). See <code>public/config.example.json</code>.</p></div>`;
    return;
  }

  const mgr = createUserManager(cfg, window.location.href, {
    automaticSilentRenew: true,
  });
  mgr.events.addSilentRenewError((err) => {
    console.warn("OIDC silent renew failed", err);
  });

  function renderLogin(el: HTMLElement): void {
    el.innerHTML = `
    <div class="card">
      <h1>EC2 usage portal</h1>
      <p>Sign in with your organization account.</p>
      <button type="button" id="btn-login" class="primary">Sign in</button>
    </div>`;
    el.querySelector("#btn-login")?.addEventListener("click", () => {
      void mgr.signinRedirect();
    });
  }

  function renderDenied(el: HTMLElement): void {
    el.innerHTML = `
    <div class="card error">
      <h1>Access denied</h1>
      <p>You are signed in but do not have access to this application.</p>
      <button type="button" id="logout">Sign out</button>
    </div>`;
    wireSignOut(el, mgr);
  }

  function renderDashboard(
    el: HTMLElement,
    userName: string,
    month: string,
    customerIds: string[],
    summaries: Map<string, SummaryResponse | Error>,
  ): void {
    const sections = buildCustomerSectionsHtml(
      customerIds,
      summaries,
      escapeHtml,
      secondsToHhMm,
    );
    el.innerHTML = buildDashboardPageHtml(
      userName,
      month,
      sections,
      escapeHtml,
    );

    wireSignOut(el, mgr);
    el.querySelector("#reload")?.addEventListener("click", () => {
      const inp = el.querySelector<HTMLInputElement>("#month");
      void bootDashboard(inp?.value || month);
    });
  }

  async function bootDashboard(month: string): Promise<void> {
    const user = await getActiveUser(mgr);
    const el = document.querySelector<HTMLElement>("#app");
    if (!el) return;
    if (!user?.access_token) {
      renderLogin(el);
      return;
    }
    if (
      !hasPortalRealmRole(
        user.profile as Record<string, unknown>,
        user.access_token,
        cfg.portalRealmRole,
      )
    ) {
      renderDenied(el);
      return;
    }

    let customerIds: string[];
    if (cfg.customersFromApi) {
      el.innerHTML = `
    <header class="top">
      <div><h1>EC2 usage</h1><p class="muted">Loading customer list…</p></div>
      <div class="controls">
        <button type="button" id="logout">Sign out</button>
      </div>
    </header>`;
      wireSignOut(el, mgr);
      try {
        customerIds = await fetchUsageCustomerList(
          cfg.apiBase,
          user.access_token,
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        el.innerHTML = `<div class="card error"><h1>Customers</h1><p>${escapeHtml(msg)}</p><p class="muted">Listing customers requires a staff token (same API role as usage summaries). Check <code>USAGE_STAFF_REALM_ROLES</code> and your Keycloak roles.</p><button type="button" id="logout">Sign out</button></div>`;
        wireSignOut(el, mgr);
        return;
      }
      if (customerIds.length === 0) {
        el.innerHTML = `<div class="card"><h1>No customers</h1><p class="muted">The usage API returned no customer ids yet (no OPEN instances or closed segments in the table).</p><button type="button" id="logout">Sign out</button></div>`;
        wireSignOut(el, mgr);
        return;
      }
    } else {
      customerIds = cfg.customers;
    }

    el.innerHTML = `
    <header class="top">
      <div><h1>EC2 usage</h1><p class="muted">Loading usage data…</p></div>
      <div class="controls">
        <button type="button" id="logout">Sign out</button>
      </div>
    </header>`;
    wireSignOut(el, mgr);
    const map = new Map<string, SummaryResponse | Error>();
    await Promise.all(
      customerIds.map(async (c) => {
        try {
          const s = await fetchUsageSummary(
            cfg.apiBase,
            c,
            month,
            user.access_token,
          );
          map.set(c, s);
        } catch (e) {
          map.set(c, e instanceof Error ? e : new Error(String(e)));
        }
      }),
    );
    const sub = displayNameFromProfile(user.profile);
    renderDashboard(el, sub, month, customerIds, map);
  }

  async function run(): Promise<void> {
    try {
      if (
        window.location.search.includes("code=") ||
        window.location.search.includes("state=")
      ) {
        await mgr.signinRedirectCallback();
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (e) {
      host.innerHTML = `<div class="card error"><h1>Sign-in error</h1><p>${escapeHtml(String(e))}</p><div class="signin-error-actions"><button type="button" id="retry" class="primary">Try again</button><button type="button" id="logout">Sign out</button></div></div>`;
      host.querySelector("#retry")?.addEventListener("click", () => {
        void mgr.signinRedirect();
      });
      wireSignOut(host, mgr);
      return;
    }

    const user = await getActiveUser(mgr);
    if (!user) {
      renderLogin(host);
      return;
    }
    await bootDashboard(monthNowUtc());
  }

  await run();
}

void startApp().catch((e) => {
  const root = document.querySelector<HTMLElement>("#app");
  if (root) {
    root.innerHTML = `<div class="card error"><p>${escapeHtml(String(e))}</p></div>`;
  }
});
